
import React, { createContext, useContext, useEffect, useState } from 'react';

interface ImagePreloadContextType {
  preloadImage: (url: string, priority?: 'high' | 'low') => void;
  preloadedImages: Set<string>;
  preloadLcpImage: (url: string) => void; // New function for LCP images
}

const ImagePreloadContext = createContext<ImagePreloadContextType>({
  preloadImage: () => {},
  preloadedImages: new Set(),
  preloadLcpImage: () => {}, // Add default implementation
});

export const useImagePreload = () => useContext(ImagePreloadContext);

interface ImagePreloadProviderProps {
  children: React.ReactNode;
  criticalImages?: string[];
  lcpImage?: string; // New prop for the LCP image
}

export const ImagePreloadProvider: React.FC<ImagePreloadProviderProps> = ({ 
  children, 
  criticalImages = [],
  lcpImage
}) => {
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());

  // Function to preload an image
  const preloadImage = (url: string, priority: 'high' | 'low' = 'low') => {
    if (!url || preloadedImages.has(url)) return;

    // Add to preloaded set
    setPreloadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(url);
      return newSet;
    });

    // Create preload link
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    link.fetchPriority = priority;
    document.head.appendChild(link);

    console.log(`Preloaded image: ${url} with priority: ${priority}`);
  };

  // Function specifically for LCP image preloading with highest priority
  const preloadLcpImage = (url: string) => {
    if (!url) return;
    
    // For LCP images, we need the highest priority possible
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    link.fetchPriority = 'high';
    link.setAttribute('data-lcp-image', 'true'); // Mark as LCP image for monitoring
    
    // Add it to the head before other elements for faster discovery
    if (document.head.firstChild) {
      document.head.insertBefore(link, document.head.firstChild);
    } else {
      document.head.appendChild(link);
    }
    
    // Add to preloaded set
    setPreloadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(url);
      return newSet;
    });

    console.log(`LCP image preloaded: ${url}`);
  };

  // Preload critical images on mount
  useEffect(() => {
    if (criticalImages.length > 0) {
      criticalImages.forEach(url => {
        preloadImage(url, 'high');
      });
    }
    
    // If we have an LCP image, preload it immediately
    if (lcpImage) {
      preloadLcpImage(lcpImage);
    }
  }, [lcpImage]);

  const value = {
    preloadImage,
    preloadedImages,
    preloadLcpImage
  };

  return (
    <ImagePreloadContext.Provider value={value}>
      {children}
    </ImagePreloadContext.Provider>
  );
};
